import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  TextField
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import SyncIcon from "@material-ui/icons/Sync";
import CardTitle from "@saleor/components/CardTitle";
import FormSpacer from "@saleor/components/FormSpacer";
import { FormChange } from "@saleor/hooks/useForm";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { SellerDetailsPageFormData } from "../SellerDetailsPage/SellerDetailsPages";

const useStyles = makeStyles(
  () => ({
    cardTitle: {
      "& span": {
        fontWeight: "normal"
      }
    },
    shopifyDetailsToolbar: {
      alignItems: "center",
      display: "flex",
      justifyContent: "space-between",
      width: "7rem"
    },
    showAsPassword: {
      "& input.MuiInputBase-input": {
        "-webkit-text-security": "disc"
      }
    },
    showAsText: {
      "& input.MuiInputBase-input": {
        "-webkit-text-security": "none"
      }
    },
    syncButton: {
      "& span": {
        color: "#002972",
        fontWeight: 600
      },
      "& svg": {
        margin: "0px"
      },
      minWidth: "30%"
    },
    verifyButton: {
      "& span": {
        color: "#0030FF",
        fontWeight: 600
      },
      "& svg": {
        "& path": {
          fill: "#0030FF"
        },
        margin: "0px"
      },
      "&:active": {
        "& span": {
          "& span": {
            color: "#002972"
          }
        },
        backgroundColor: "#FFCE22",
        boxShadow: "inset 0px 0px 0px 2px #D0A73E"
      },
      "&:disabled": {
        "& span": {
          color: "#BAC8FF"
        },
        backgroundColor: "#FFEDBE"
      },
      "&:hover": {
        backgroundColor: "#FFD15C",
        boxShadow: "inset 0px 0px 0px 1px #D0A73E"
      },
      backgroundColor: "#FFD15C"
    }
  }),
  { name: "ShopifySellerDetails" }
);

interface ShopifySellerDetailsProps {
  data: SellerDetailsPageFormData;
  disabled: boolean;
  handleShopifySync: () => void;
  handleVerifyShopifyConnection: (
    shopifyApiSecret: string,
    shopifyApiShop: string,
    verifyByBrandId: boolean
  ) => void;
  onChange: FormChange;
  isLoading?: boolean;
  isShopifyVerifyCredentialsLoading?: boolean;
}

const ShopifySellerDetails: React.FC<ShopifySellerDetailsProps> = ({
  data,
  disabled,
  handleShopifySync,
  handleVerifyShopifyConnection,
  isLoading,
  isShopifyVerifyCredentialsLoading,
  onChange
}) => {
  const classes = useStyles({});
  const intl = useIntl();
  const [showApiSecret, setShowApiSecret] = useState(false);

  return (
    <Card>
      <div>
        <CardTitle
          className={classes.cardTitle}
          title={intl.formatMessage({
            defaultMessage: "Shopify Details",
            description: "Shopify Details"
          })}
          toolbar={
            <div className={classes.shopifyDetailsToolbar}>
              <Button
                className={classes.verifyButton}
                id="verifyBtn"
                variant="text"
                onClick={() =>
                  handleVerifyShopifyConnection(
                    data.shopifyApiSecret,
                    data.shopifyApiShop,
                    false
                  )
                }
              >
                {isShopifyVerifyCredentialsLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  "Verify"
                )}
              </Button>
              <Button
                className={classes.syncButton}
                id="refreshverfyBtn"
                variant="text"
                onClick={handleShopifySync}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <SyncIcon style={{ fill: "#0030FF" }} />
                )}
              </Button>
            </div>
          }
        />
      </div>
      <CardContent>
        {/* Follow SellerInfo for TextField props */}
        <TextField
          id="shopNameTxt"
          label={intl.formatMessage({
            defaultMessage: "Shop Name",
            description: "Shop Name"
          })}
          name="shopifyApiShop"
          type="text"
          value={data?.shopifyApiShop || ""}
          onChange={onChange}
          disabled={disabled}
          fullWidth
        />
        <FormSpacer />
        <TextField
          id="shopifyAPIKeyTxt"
          label={intl.formatMessage({
            defaultMessage: "Shopify API Key",
            description: "Shopify API Key"
          })}
          name="shopifyApiKey"
          type="text"
          value={data?.shopifyApiKey || ""}
          onChange={onChange}
          disabled={disabled}
          fullWidth
        />
        <FormSpacer />
        <TextField
          id="apiSecretTxt"
          label={intl.formatMessage({
            defaultMessage: "API Secret",
            description: "API Secret"
          })}
          InputProps={{
            className: showApiSecret
              ? classes.showAsText
              : classes.showAsPassword,
            endAdornment: (
              <IconButton
                color="primary"
                id="unhideBtn"
                onClick={() => setShowApiSecret(v => !v)}
                onMouseDown={e => e.preventDefault()}
              >
                {showApiSecret ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            ),
            readOnly: true
          }}
          name="shopifyApiSecret"
          type="text"
          autoComplete="off"
          value={data?.shopifyApiSecret || ""}
          onChange={onChange}
          onFocus={e => e.target.removeAttribute("readOnly")}
          onBlur={e => e.target.setAttribute("readonly", "readonly")}
          disabled={disabled}
          fullWidth
          onCopy={e => e.preventDefault()}
        />
        <FormSpacer />
      </CardContent>
    </Card>
  );
};

ShopifySellerDetails.displayName = "ShopifySellerDetails";
export default ShopifySellerDetails;
